import * as THREE from "three";
import type { CountryFeature } from "../types";

const R_DOTS = 100.9; // the bright fiber tips
const R_ILLUM = 96.1; // halo layer just above the core so it "casts" onto it

// match three-globe's polar2Cartesian so dots align with polygon outlines
function latLngToXYZ(lat: number, lng: number, r: number) {
  const phi = THREE.MathUtils.degToRad(90 - lat);
  const theta = THREE.MathUtils.degToRad(90 - lng);
  const sp = Math.sin(phi);
  return [r * sp * Math.cos(theta), r * Math.cos(phi), r * sp * Math.sin(theta)] as const;
}

function pointInRing(x: number, y: number, ring: number[][]) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersects =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function pointInPolygon(x: number, y: number, polygon: number[][][]) {
  const outer = polygon[0];
  if (!outer || !pointInRing(x, y, outer)) return false;
  for (let i = 1; i < polygon.length; i++) {
    if (pointInRing(x, y, polygon[i])) return false;
  }
  return true;
}

export interface GeneratedDots {
  positions: Float32Array;
  phases: Float32Array;
  sizes: Float32Array;
  count: number;
}

export function generateCountryDots(
  features: CountryFeature[],
  density = 1.6
): GeneratedDots {
  const positions: number[] = [];
  const phases: number[] = [];
  const sizes: number[] = [];

  for (const feature of features) {
    const geom = feature.geometry;
    if (!geom) continue;
    const polygons: number[][][][] =
      geom.type === "MultiPolygon"
        ? (geom.coordinates as number[][][][])
        : [geom.coordinates as number[][][]];

    for (const polygon of polygons) {
      const outer = polygon[0];
      if (!outer || outer.length < 3) continue;

      let minLng = 180;
      let maxLng = -180;
      let minLat = 90;
      let maxLat = -90;
      for (const [lng, lat] of outer) {
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }

      const latSpan = maxLat - minLat;
      const lngSpan = maxLng - minLng;
      if (latSpan <= 0 || lngSpan <= 0) continue;

      const midLatRad = THREE.MathUtils.degToRad((minLat + maxLat) / 2);
      const area = Math.max(
        1,
        Math.abs(latSpan * lngSpan * Math.cos(midLatRad))
      );
      const target = Math.min(2400, Math.max(8, Math.floor(area * density)));
      const maxAttempts = target * 18;

      let kept = 0;
      let attempts = 0;
      while (kept < target && attempts < maxAttempts) {
        attempts++;
        const lng = minLng + Math.random() * lngSpan;
        const lat = minLat + Math.random() * latSpan;
        if (!pointInPolygon(lng, lat, polygon)) continue;

        const r = R_DOTS + (Math.random() - 0.5) * 0.25;
        const [x, y, z] = latLngToXYZ(lat, lng, r);
        positions.push(x, y, z);
        phases.push(Math.random() * Math.PI * 2);
        // thicker base size — fibre tips reading as plump pinpoints
        sizes.push(2.8 + Math.pow(Math.random(), 4) * 3.8);
        kept++;
      }
    }
  }

  return {
    positions: new Float32Array(positions),
    phases: new Float32Array(phases),
    sizes: new Float32Array(sizes),
    count: positions.length / 3,
  };
}

// -----------------------------------------------------------------------------
// Ripple waves — N concurrent expanding wavefronts from random epicenters,
// shared by the dot layer and the illumination halo layer below it.
// -----------------------------------------------------------------------------

export const RIPPLE_COUNT = 4;

export interface RippleUniforms {
  uRippleCenters: { value: THREE.Vector3[] };
  uRippleProgress: { value: number[] };
  uRippleAmp: { value: number };
}

export function createRippleUniforms(): RippleUniforms {
  return {
    uRippleCenters: {
      value: Array.from(
        { length: RIPPLE_COUNT },
        () => new THREE.Vector3(1, 0, 0)
      ),
    },
    uRippleProgress: {
      value: new Array(RIPPLE_COUNT).fill(0),
    },
    uRippleAmp: { value: 2.4 },
  };
}

// shared GLSL snippet — summed ripple boost in [0..~N]
const RIPPLE_GLSL = /* glsl */ `
  uniform vec3 uRippleCenters[${RIPPLE_COUNT}];
  uniform float uRippleProgress[${RIPPLE_COUNT}];
  uniform float uRippleAmp;

  float computeRipple(vec3 np, vec3 c, float t) {
    if (t >= 1.0 || t <= 0.0) return 0.0;
    float adCos = dot(np, c);
    float ad = acos(clamp(adCos, -1.0, 1.0));
    float front = t * 3.14159;          // radians travelled across sphere
    float sharpness = 13.0;             // tighter wavefront — less spread
    float wave = exp(-pow((ad - front) * sharpness, 2.0));
    float fadeIn = smoothstep(0.0, 0.08, t);
    float fadeOut = 1.0 - smoothstep(0.80, 1.0, t);
    return wave * fadeIn * fadeOut;
  }

  float ripplesAt(vec3 np) {
    float sum = 0.0;
    for (int i = 0; i < ${RIPPLE_COUNT}; i++) {
      sum += computeRipple(np, uRippleCenters[i], uRippleProgress[i]);
    }
    return sum;
  }
`;

// -----------------------------------------------------------------------------
// Main fiber-optic dot layer
// -----------------------------------------------------------------------------

export function makeCountryPoints(
  data: GeneratedDots,
  sharedTime: { value: number },
  rippleUniforms: RippleUniforms
): THREE.Points {
  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.BufferAttribute(data.positions, 3));
  geom.setAttribute("aPhase", new THREE.BufferAttribute(data.phases, 1));
  geom.setAttribute("aSize", new THREE.BufferAttribute(data.sizes, 1));

  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
    uniforms: {
      uTime: sharedTime,
      uColor: { value: new THREE.Color("#ffffff") },
      ...rippleUniforms,
    },
    vertexShader: /* glsl */ `
      attribute float aPhase;
      attribute float aSize;
      uniform float uTime;
      varying float vTw;

      ${RIPPLE_GLSL}

      void main() {
        // dots ride the wave — barely perceptible vertical bump
        float ripple = ripplesAt(normalize(position));
        vec3 lifted = position * (1.0 + ripple * 0.004);
        vec4 mv = modelViewMatrix * vec4(lifted, 1.0);
        gl_Position = projectionMatrix * mv;

        float breath = 0.88 + 0.12 * sin(uTime * 0.25);
        float personal = 0.90 + 0.10 * pow(0.5 + 0.5 * sin(uTime * 0.9 + aPhase), 1.6);

        // softer brightness crest as the wave passes
        vTw = personal * breath + ripple * 0.22;
        float sizeBoost = 1.0 + ripple * 0.10;

        gl_PointSize = aSize * sizeBoost * (310.0 / -mv.z) * (0.9 + vTw * 0.1);
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      varying float vTw;
      uniform vec3 uColor;
      void main() {
        vec2 c = gl_PointCoord - 0.5;
        float d = length(c) * 2.0;
        // thicker fibre — wider core + broad halo
        float core = exp(-d * d * 7.0);
        float halo = exp(-d * d * 1.2) * 0.55;
        float intensity = (core + halo) * vTw;
        if (intensity < 0.01) discard;
        gl_FragColor = vec4(uColor * intensity, intensity);
      }
    `,
  });

  const pts = new THREE.Points(geom, mat);
  pts.name = "atlas-country-points";
  pts.frustumCulled = false;
  return pts;
}

// -----------------------------------------------------------------------------
// Illumination halos — same epicentres but projected onto the core surface,
// much larger/softer. Additive: these visually "cast" light from the dots
// above onto the black sphere below.
// -----------------------------------------------------------------------------

export function makeIlluminationHalos(
  data: GeneratedDots,
  sharedTime: { value: number },
  rippleUniforms: RippleUniforms
): THREE.Points {
  const src = data.positions;
  const out = new Float32Array(src.length);
  for (let i = 0; i < src.length; i += 3) {
    const x = src[i];
    const y = src[i + 1];
    const z = src[i + 2];
    const len = Math.hypot(x, y, z) || 1;
    const s = R_ILLUM / len;
    out[i] = x * s;
    out[i + 1] = y * s;
    out[i + 2] = z * s;
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.BufferAttribute(out, 3));
  geom.setAttribute("aPhase", new THREE.BufferAttribute(data.phases, 1));
  geom.setAttribute("aSize", new THREE.BufferAttribute(data.sizes, 1));

  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
    uniforms: {
      uTime: sharedTime,
      uColor: { value: new THREE.Color("#ffe8d4") },
      ...rippleUniforms,
    },
    vertexShader: /* glsl */ `
      attribute float aPhase;
      attribute float aSize;
      uniform float uTime;
      varying float vTw;

      ${RIPPLE_GLSL}

      void main() {
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mv;

        float breath = 0.70 + 0.20 * sin(uTime * 0.32);
        float personal = 0.70 + 0.30 * pow(0.5 + 0.5 * sin(uTime * 1.1 + aPhase), 1.6);

        // the GLOW is what the sweep modulates — dominant ripple contribution here
        float ripple = ripplesAt(normalize(position));

        vTw = personal * breath + ripple * 0.32;

        // halo spreads gently as the wave crests over it
        float sizeBoost = 1.0 + ripple * 0.22;
        gl_PointSize = aSize * 5.2 * sizeBoost * (310.0 / -mv.z);
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      varying float vTw;
      uniform vec3 uColor;
      void main() {
        vec2 c = gl_PointCoord - 0.5;
        float d = length(c) * 2.0;
        // soft wide halo — light spill on the surface, no hot core
        float halo = exp(-d * d * 1.4);
        float intensity = halo * vTw * 0.14;
        if (intensity < 0.01) discard;
        gl_FragColor = vec4(uColor * intensity, intensity);
      }
    `,
  });

  const pts = new THREE.Points(geom, mat);
  pts.name = "atlas-illumination";
  pts.frustumCulled = false;
  return pts;
}
